import React, { createContext, useEffect, useState } from "react";
import UserPool from "./UserPool";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import qs from "qs";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import * as pixel from "./../fpixel";
import Cookies from "js-cookie";
import { COGNITO_STORAGE_OBJECT } from "../../config";

const AccountContext = createContext();

const Account = (props) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [idToken, setIdToken] = useState();
  const [accessToken, setAccessToken] = useState();
  const [sessionLoaded, setSessionLoaded] = useState(false);

  const getSession = async () => {
    return await new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.getSession(async (err, session) => {
          if (err) {
            setCurrentUser(null);
            reject();
          } else {
            if (session?.idToken?.jwtToken && session?.accessToken?.jwtToken) {
              setIdToken(session?.idToken?.jwtToken);
              setAccessToken(session?.accessToken?.jwtToken);
              setCurrentUser(user);
              resolve(session);

              // Get email from token
              let email = session?.idToken?.payload?.email;

              setUserAttributes({
                email: email,
                "custom:family_name":
                  session?.idToken?.payload?.["custom:family_name"],
                "custom:given_name":
                  session?.idToken?.payload?.["custom:given_name"],
              });
            } else {
              setIdToken(null);
              setAccessToken(null);
              setCurrentUser(null);
              reject();
            }
          }
        });
      } else {
        setIdToken(null);
        setAccessToken(null);
        setCurrentUser(null);
        reject();
      }
    }).catch((err) => {
      setIdToken(null);
      setAccessToken(null);
      setCurrentUser(null);
    });
  };

  useEffect(() => {
    setSessionLoaded(false);

    // If there is access_token and id_token in the url, then authenticate user with those tokens
    if (
      // ID Token
      new URLSearchParams(window.location.search).get("IdToken") &&
      // Access Token
      new URLSearchParams(window.location.search).get("AccessToken")
    ) {
      logOut().then(() => {
        setSessionLoaded(true);
      });
      return;
    }

    getSession()
      .then((session) => {
        setSessionLoaded(true);
      })
      .catch((err) => {
        setSessionLoaded(true);
      });
  }, []);

  const refreshTokens = async () => {
    return await new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.getSession((err, session) => {
          if (err) {
            // Should we logout user here?
            reject();
          } else {
            user.getUserAttributes(function (err, result) {
              if (err) {
                setUserAttributes(null);
                return;
              }
              let temp = {};
              for (let i of result) {
                temp[i.Name] = i.Value;
              }
              setUserAttributes(temp);
              if (
                session?.idToken?.jwtToken &&
                session?.accessToken?.jwtToken
              ) {
                setIdToken(session.idToken.jwtToken);
                setAccessToken(session.accessToken.jwtToken);
                resolve(session);
              } else {
                // Should we logout user here?
                reject();
              }
            });
          }
        });
      } else {
        // Should we logout user here?
        reject();
      }
    }).catch((err) => {
      // Should we logout user here?
    });
  };

  const signUp = async (Email, Firstname, Lastname, Password) => {
    return await new Promise((resolve, reject) => {
      const emailAttribute = new CognitoUserAttribute({
        Name: "email",
        Value: Email,
      });
      const firstName = new CognitoUserAttribute({
        Name: "given_name",
        Value: Firstname,
      });
      const lastName = new CognitoUserAttribute({
        Name: "family_name",
        Value: Lastname,
      });

      // Save first name and last name in session storage
      sessionStorage.setItem("firstName", Firstname);
      sessionStorage.setItem("lastName", Lastname);

      UserPool.signUp(
        Email,
        Password,
        [emailAttribute, firstName, lastName],
        null,
        (err, data) => {
          if (err) {
            reject(err);
            return;
          }
          window.POPER_UPDATE_USER_ACQUISITION = true;
          resolve(data);

          try {
            var facebookCookie = Cookies.get("is-facebook-referrer");

            console.log(facebookCookie);

            if (facebookCookie) {
              console.log("Sending pixel event");
              // pixel.event("CompleteRegistration");
              // Delete the cookie
              Cookies.remove("is-facebook-referrer", { domain: ".poper.ai" });
            }
          } catch (error) {
            console.log(error);
          }

          try {
            var linkedinCookie = Cookies.get("is-linkedin-referrer");

            console.log(linkedinCookie);

            if (linkedinCookie) {
              console.log("Sending insight event");
              // window.lintrk("track", { conversion_id: 17286460 });
              // Delete the cookie
              Cookies.remove("is-linkedin-referrer", { domain: ".poper.ai" });
            }
          } catch (error) {
            console.log(error);
          }
        },
      );
    });
  };

  const authenticate = async (Email, Password, TotpCode) => {
    return await new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: Email,
        Pool: UserPool,
        ...COGNITO_STORAGE_OBJECT,
      });

      const authDetails = new AuthenticationDetails({
        Username: Email,
        Password: Password,
      });

      user.authenticateUser(authDetails, {
        onSuccess: (data) => {
          window.POPER_UPDATE_USER_ACQUISITION = true;
          getSession()
            .then((session) => {
              resolve(data);
            })
            .catch((err) => {
              reject(err);
            });
        },
        totpRequired: (err) => {
          if (TotpCode) {
            user.sendMFACode(
              TotpCode,
              {
                onSuccess: (data) => {
                  window.POPER_UPDATE_USER_ACQUISITION = true;
                  getSession()
                    .then((session) => {
                      resolve(data);
                    })
                    .catch((err) => {
                      reject(err);
                    });
                },

                onFailure: (err) => {
                  getSession();
                  reject(err);
                },
              },
              "SOFTWARE_TOKEN_MFA",
            );

            return;
          }

          getSession();
          reject({
            code: "TOTPException",
          });
        },
        onFailure: (err) => {
          getSession();
          reject(err);
        },
      });
      return;
    });
  };

  const generateCodeForMFA = async () => {
    return await new Promise((resolve, reject) => {
      currentUser.associateSoftwareToken({
        associateSecretCode: (secretCode) => {
          resolve(secretCode);
        },
        onFailure: (err) => {
          console.log(err);
          reject(err);
        },
      });
    });
  };

  const verifyAndEnableMFA = async (totpCode) => {
    return await new Promise((resolve, reject) => {
      currentUser.verifySoftwareToken(totpCode, "My TOTP device", {
        onSuccess: function (session) {
          var totpMfaSettings = {
            PreferredMfa: true,
            Enabled: true,
          };

          currentUser.setUserMfaPreference(
            null,
            totpMfaSettings,
            function (err, result) {
              if (err) {
                reject(err);
                return;
              }
              getSession();
              resolve(result);
            },
          );
        },
        onFailure: function (error) {
          console.log(error);
          reject(error);
        },
      });
    });
  };

  const disableMFA = async () => {
    return await new Promise((resolve, reject) => {
      var totpMfaSettings = {
        PreferredMfa: false,
        Enabled: false,
      };
      currentUser.setUserMfaPreference(
        null,
        totpMfaSettings,
        function (err, result) {
          if (err) {
            console.log(err);
            reject(err);
            return;
          }
          getSession();
          resolve(result);
        },
      );
    });
  };

  const authenticateWithTokens = async (
    Email,
    AccessToken,
    IdToken,
    RefreshToken,
    TokenType,
    ExpiresIn,
  ) => {
    return await new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: Email,
        Pool: UserPool,
        ...COGNITO_STORAGE_OBJECT,
      });

      user.signInUserSession = user.getCognitoUserSession({
        AccessToken,
        IdToken,
        // RefreshToken,
        TokenType,
        ExpiresIn,
      });

      user.cacheTokens();

      getSession()
        .then((session) => {
          resolve();
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        });
    });
  };

  const verifyUserAccount = async (Email, code) => {
    return await new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: Email,
        Pool: UserPool,
        ...COGNITO_STORAGE_OBJECT,
      });

      user.confirmRegistration(code, true, function (err, result) {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const sendUserVerificationCode = async (Email) => {
    return await new Promise((resolve, reject) => {
      axios
        .post(
          process.env.REACT_APP_POPER_API_URL + "/general/user/get_username",
          qs.stringify({ email: Email }),
        )
        .then((res) => {
          var Username = res?.data?.Output?.username;

          if (!Username) {
            reject();
            return;
          }

          const user = new CognitoUser({
            Username: Username,
            Pool: UserPool,
            ...COGNITO_STORAGE_OBJECT,
          });

          user.resendConfirmationCode(function (err, result) {
            if (err) {
              reject(err);
              return;
            }
            resolve(result);
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const changeUserPassword = async (Email, code, password) => {
    return await new Promise((resolve, reject) => {
      var cognitoUser = new CognitoUser({
        Username: Email,
        Pool: UserPool,
        ...COGNITO_STORAGE_OBJECT,
      });

      cognitoUser.confirmPassword(code, password, {
        onSuccess: function (data) {
          resolve(data);
        },
        onFailure: function (err) {
          console.log(err);
          reject(err);
        },
      });
    });
  };

  const sendForgotPasswordCode = async (Email) => {
    return await new Promise((resolve, reject) => {
      var cognitoUser = new CognitoUser({
        Username: Email,
        Pool: UserPool,
        ...COGNITO_STORAGE_OBJECT,
      });

      cognitoUser.forgotPassword({
        onSuccess: function (data) {
          resolve(data);
        },
        onFailure: function (err) {
          if (err.code === "UserNotFoundException") {
            var endpoint;
            if (
              !process.env.REACT_APP_NODE_ENV ||
              process.env.REACT_APP_NODE_ENV === "development"
            ) {
              endpoint = "http://localhost:3607/general/user/existance";
            } else {
              endpoint =
                process.env.REACT_APP_POPER_API_URL + "/general/user/existance";
            }

            axios
              .post(endpoint, qs.stringify({ email: Email }))
              .then((res) => {
                console.log("User does not exist");
                reject({
                  code: "UserNotFoundException",
                  message: "User does not exist",
                });
              })
              .catch((err) => {
                var endpoint;
                if (
                  !process.env.REACT_APP_NODE_ENV ||
                  process.env.REACT_APP_NODE_ENV === "development"
                ) {
                  endpoint = "http://localhost:3607/general/user/create-user";
                } else {
                  endpoint =
                    process.env.REACT_APP_POPER_API_URL +
                    "/general/user/create-user";
                }

                axios
                  .post(endpoint, qs.stringify({ email: Email }))
                  .then((res) => {
                    console.log("User created");
                    sendForgotPasswordCode(Email)
                      .then((res) => {
                        resolve(res);
                      })
                      .catch((err) => {
                        reject(err);
                      });
                  })
                  .catch((err) => {
                    console.log(err);
                    reject(err);
                  });
              });
          }
          if (
            err.code === "InvalidParameterException" &&
            err.message.includes("verified")
          ) {
            var endpoint;
            if (
              !process.env.REACT_APP_NODE_ENV ||
              process.env.REACT_APP_NODE_ENV === "development"
            ) {
              endpoint =
                "http://localhost:3607/general/user/reset-verification";
            } else {
              endpoint =
                process.env.REACT_APP_POPER_API_URL +
                "/general/user/reset-verification";
            }

            axios
              .post(endpoint, qs.stringify({ email: Email }))
              .then((res) => {
                console.log("User reset verification");
                sendForgotPasswordCode(Email)
                  .then((res) => {
                    resolve(res);
                  })
                  .catch((err) => {
                    reject(err);
                  });
              })
              .catch((err) => {
                console.log(err);
                reject(err);
              });
          } else {
            console.log(err);
            reject(err);
          }
        },
      });
    });
  };

  const logOut = async () => {
    return await new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.signOut((data) => {
          getSession();
          resolve();
        });
      } else {
        getSession();
        resolve();
      }
    });
  };

  const signInFederated = (identityProvider) => {
    let authHost = "https://poper.auth.us-east-1.amazoncognito.com";

    let redirectUri;
    if (
      !process.env.REACT_APP_NODE_ENV ||
      process.env.REACT_APP_NODE_ENV === "development"
    ) {
      redirectUri = `${
        window.location.protocol + "//" + window.location.host
      }/oauth`;
    } else {
      redirectUri = process.env.REACT_APP_POPER_DEPLOY_URL + "/oauth/";
    }
    let responseType = "code";
    let clientId = process.env.REACT_APP_AWS_COGNITO_CLIENT;
    let state = "some_state";
    let scope = "profile email openid aws.cognito.signin.user.admin";
    let access_type = "offline_access";
    let code_challenge = "adsasasdibausydbuasbduhyb";
    let authUrl = `${authHost}/oauth2/authorize?identity_provider=${identityProvider}&code=${code_challenge}&redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&state=${state}&scope=${scope}&access_type=${access_type}`;

    var childWindow = window.open(
      authUrl,
      "awsGoogleAuth",
      "location,toolbar,resizable,scrollbars,status,width=600,height=600",
    );

    var maxTries = 0;

    window.addEventListener(
      "message",
      (res) => {
        if (res.data.action === "request_again") {
          // Only request maximum of 5 times.
          if (maxTries > 4) {
            childWindow.close();
            return;
          }
          childWindow.location.replace(authUrl);
          maxTries++;

          return;
        }
        if (res.data.action === "login") {
          let tokensData = res.data.data;

          axios
            .post(
              process.env.REACT_APP_POPER_API_URL +
                "/general/user/oauth_username",
              qs.stringify({ access_token: tokensData.AccessToken }),
            )
            .then((res) => {
              var user = new CognitoUser({
                Username: res?.data?.Output?.data?.Username,
                Pool: UserPool,
                ...COGNITO_STORAGE_OBJECT,
              });

              user.signInUserSession = user.getCognitoUserSession(tokensData);
              user.cacheTokens();
              getSession();

              window.POPER_UPDATE_USER_ACQUISITION = true;

              try {
                var facebookCookie = Cookies.get("is-facebook-referrer");

                console.log(facebookCookie);

                if (facebookCookie) {
                  console.log("Sending pixel event");
                  // pixel.event("CompleteRegistration");
                  // Delete the cookie
                  Cookies.remove("is-facebook-referrer", {
                    domain: ".poper.ai",
                  });
                }
              } catch (error) {
                console.log(error);
              }

              return;
            })
            .catch((err) => {
              console.log(err);
            });
        }
      },
      false,
    );
  };

  const signInFederatedGoogle = () => {
    let authHost = "https://accounts.google.com";

    let redirectUri;
    if (
      !process.env.REACT_APP_NODE_ENV ||
      process.env.REACT_APP_NODE_ENV === "development"
    ) {
      // let authHost = "https://poper.auth.us-east-1.amazoncognito.com";
      redirectUri = `https://poper.auth.us-east-1.amazoncognito.com/oauth2/idpresponse`;
    } else {
      redirectUri = process.env.REACT_APP_POPER_DEPLOY_URL + "/oauth/";
    }
    let responseType = "code";
    let clientId =
      "796804839287-vvnm4no80o12bdm12af1n2sbg4drfimu.apps.googleusercontent.com";
    let scope = "profile email openid";
    let authUrl = `${authHost}/o/oauth2/v2/auth?redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&scope=${scope}&prompt=consent&access_type=offline`;

    console.log(authUrl);

    var childWindow = window.open(
      authUrl,
      "awsGoogleAuth",
      "location,toolbar,resizable,scrollbars,status,width=600,height=600",
    );

    var maxTries = 0;

    window.addEventListener(
      "message",
      (res) => {
        if (res.data.action === "request_again") {
          // Only request maximum of 5 times.
          if (maxTries > 4) {
            childWindow.close();
            return;
          }
          console.log("retrying");
          childWindow.location.replace(authUrl);
          maxTries++;

          return;
        }
        if (res.data.action === "login") {
          let tokensData = res.data.data;

          axios
            .post(
              process.env.REACT_APP_POPER_API_URL +
                "/general/user/oauth_username",
              qs.stringify({ access_token: tokensData.AccessToken }),
            )
            .then((res) => {
              var user = new CognitoUser({
                Username: res?.data?.Output?.data?.Email,
                Pool: UserPool,
                ...COGNITO_STORAGE_OBJECT,
              });

              user.signInUserSession = user.getCognitoUserSession(tokensData);
              user.cacheTokens();
              getSession();
              return;
            })
            .catch((err) => {
              console.log(err);
            });
        }
      },
      false,
    );
  };

  useEffect(() => {
    // Refresh Tokens every 120 seconds
    const timer = setInterval(() => {
      refreshTokens();
    }, 2 * 30 * 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <AccountContext.Provider
      value={{
        authenticate,
        generateCodeForMFA,
        verifyAndEnableMFA,
        disableMFA,
        signUp,
        getSession,
        logOut,
        currentUser,
        userAttributes,
        idToken,
        accessToken,
        signInFederated,
        signInFederatedGoogle,
        sessionLoaded,
        setSessionLoaded,
        verifyUserAccount,
        sendUserVerificationCode,
        changeUserPassword,
        sendForgotPasswordCode,
        authenticateWithTokens,
      }}
    >
      {props.children}
    </AccountContext.Provider>
  );
};
export { Account, AccountContext };
